import {
    GroupChart,
    Identifier,
    Product,
    TranslatedGroupChart,
    TranslatedProduct,
    Text,
    Dependency,
} from "../types/PersologOnlineTypes.ts";
import { LanguageManagementAPI, ProfileAdministrationAPI } from "../api";
import { i18n } from "../main.ts";
import * as LanguageUtils from "./LanguageUtils.ts";
export { extractLanguages } from "./LanguageUtils.ts";

/** Everything the public Language Overview page renders. */
export type LanguageOverviewData = {
    products: TranslatedProduct[];
    groupCharts: TranslatedGroupChart[];
};

/**
 * The display name of an identifier in the active locale, falling back to English and then German
 * before giving up and showing the raw identifier.
 */
function translate(identifiers: Identifier[], identifier: string): string {
    const texts = identifiers.find((id: Identifier) => id.identifier === identifier)?.texts;
    const textObj =
        texts?.find((t: Text) => t.language === i18n.global.locale.value) ??
        texts?.find((t: Text) => t.language === "en") ??
        texts?.find((t: Text) => t.language === "de");
    return textObj?.text || identifier;
}

const byIdentifier = (a: { productNo: string }, b: { productNo: string }) =>
    a.productNo.localeCompare(b.productNo, undefined, { numeric: true });

/**
 * Fetches the products and the group charts for the public Language Overview page.
 *
 * Group charts are not rows in the products table, so they come from their own endpoint (RDT-403).
 * A handful of them (in119, in121, in122) do have a leftover products row - in119 has been the
 * group chart rather than a sellable profile since the individual product was renamed to in129 -
 * so the products list is stripped of every group chart id to keep each product in exactly one
 * table.
 *
 * TODO: Filter activated products in the backend directly
 */
export async function fetchLanguageOverviewData(): Promise<LanguageOverviewData> {
    const [allProducts, allGroupCharts, productIdentifiers, modelIdentifiers, activatedLanguages] = await Promise.all([
        ProfileAdministrationAPI.fetchPublicProducts(),
        ProfileAdministrationAPI.fetchPublicGroupCharts(),
        LanguageManagementAPI.fetchPublicProductNames(),
        LanguageManagementAPI.fetchPublicModelNames(),
        LanguageManagementAPI.fetchPublicLanguages(),
    ]);

    const languagesByProduct = LanguageUtils.extractLanguages(activatedLanguages);
    const groupChartIds = new Set(allGroupCharts.map((groupChart: GroupChart) => groupChart.groupChart));

    const products: TranslatedProduct[] = allProducts
        .filter((product: Product) => !groupChartIds.has(product.productNo))
        .map((product: Product) => ({
            productName: translate(productIdentifiers, product.productNo),
            productNo: product.productNo,
            quantity: 0,
            shopLink: "",
            modelName: translate(modelIdentifiers, product.model.model),
            modelNo: product.model.model,
            rank: product.model.rank,
            profileLanguages: languagesByProduct[product.productNo] || [],
            questionnaireLanguages: languagesByProduct[`questionnaire-${product.productNo}`] || [],
        }))
        // Public access: only products that are actually activated in at least one language
        .filter(
            (product: TranslatedProduct) =>
                (product.profileLanguages.length > 0 || product.questionnaireLanguages.length > 0) &&
                product.productName &&
                product.productNo,
        )
        .sort(byIdentifier);

    const groupCharts: TranslatedGroupChart[] = allGroupCharts
        .map((groupChart: GroupChart) => {
            // A group chart has no model of its own, it inherits the one of the products it aggregates.
            const models = [...new Set(groupChart.products.products.map((product: Product) => product.model.model))];
            return {
                groupChartName: translate(productIdentifiers, groupChart.groupChart),
                groupChartNo: groupChart.groupChart,
                modelName: models.map((model: string) => translate(modelIdentifiers, model)).join(", "),
                modelNo: models.join(", "),
                groupChartLanguages: languagesByProduct[groupChart.groupChart] || [],
            };
        })
        .filter((groupChart: TranslatedGroupChart) => groupChart.groupChartLanguages.length > 0)
        .sort((a, b) => byIdentifier({ productNo: a.groupChartNo }, { productNo: b.groupChartNo }));

    return { products, groupCharts };
}

export async function getTextDependencies(stage: string = "STAGING"):  Promise<Dependency[]>  {
    try {
        if (stage !== "STAGING" && stage !== "PROD") {
            throw new Error("Invalid stage. Must be 'STAGING' or 'PROD'.");
        }
        const dependencies = await LanguageManagementAPI.getTextDependencies(stage);
        return dependencies;
    } catch (error) {
        console.error("Error fetching text dependencies", error);
        return [];
    }
}
